<template>
    <div class="rafiq-pg" v-if="guide" dir="rtl">

        <!-- ===== Page header strip: title + the small (i) button ===== -->
        <div class="rafiq-pg-bar">
            <span class="rafiq-pg-avatar">
                <iconify-icon :icon="guide.icon"></iconify-icon>
            </span>

            <div class="rafiq-pg-head">
                <div class="rafiq-pg-titleline">
                    <h1 class="rafiq-pg-title">{{ guide.title }}</h1>

                    <!-- The whole point of this component. Small, quiet, always
                         in the same place on every page. -->
                    <button type="button"
                            class="rafiq-pg-info"
                            :class="{ active: open }"
                            @click="openGuide"
                            :aria-expanded="open ? 'true' : 'false'"
                            aria-label="ما هذه الصفحة؟"
                            title="ما هذه الصفحة؟ — شرح كامل">
                        <iconify-icon icon="solar:info-circle-line-duotone"></iconify-icon>
                    </button>
                </div>
                <p class="rafiq-pg-tagline" v-if="guide.tagline">{{ guide.tagline }}</p>
            </div>

            <span class="rafiq-pg-group" v-if="guide.groupLabel">{{ guide.groupLabel }}</span>
        </div>

        <!-- ===== Full guide modal ===== -->
        <div class="rafiq-pg-backdrop" v-if="open" @click.self="close">
            <div class="rafiq-pg-modal" role="dialog" aria-modal="true" aria-labelledby="rafiq-pg-modal-title">

                <div class="rafiq-pg-modal-head">
                    <span class="rafiq-pg-avatar lg">
                        <iconify-icon :icon="guide.icon"></iconify-icon>
                    </span>
                    <div class="rafiq-pg-modal-headtext">
                        <h2 class="rafiq-pg-modal-title" id="rafiq-pg-modal-title">{{ guide.title }}</h2>
                        <p class="rafiq-pg-modal-sub" v-if="guide.tagline">{{ guide.tagline }}</p>
                    </div>
                    <button type="button" class="rafiq-pg-close" @click="close" aria-label="إغلاق">
                        <iconify-icon icon="solar:close-circle-line-duotone"></iconify-icon>
                    </button>
                </div>

                <div class="rafiq-pg-modal-body">

                    <p class="rafiq-pg-what" v-if="guide.what">{{ guide.what }}</p>

                    <section class="rafiq-pg-section" v-if="guide.shows.length">
                        <h3 class="rafiq-pg-section-title">
                            <iconify-icon icon="solar:eye-line-duotone"></iconify-icon>
                            ماذا تعرض هذه الصفحة
                        </h3>
                        <ul class="rafiq-pg-list">
                            <li v-for="(line, i) in guide.shows" :key="'s' + i">{{ line }}</li>
                        </ul>
                    </section>

                    <section class="rafiq-pg-section" v-if="guide.actions.length">
                        <h3 class="rafiq-pg-section-title">
                            <iconify-icon icon="solar:cursor-line-duotone"></iconify-icon>
                            ماذا يمكنك أن تفعل هنا
                        </h3>
                        <ul class="rafiq-pg-list do">
                            <li v-for="(line, i) in guide.actions" :key="'a' + i">{{ line }}</li>
                        </ul>
                    </section>

                    <section class="rafiq-pg-section" v-if="guide.notes.length">
                        <h3 class="rafiq-pg-section-title warn">
                            <iconify-icon icon="solar:shield-warning-line-duotone"></iconify-icon>
                            انتبه
                        </h3>
                        <ul class="rafiq-pg-list warn">
                            <li v-for="(line, i) in guide.notes" :key="'n' + i">{{ line }}</li>
                        </ul>
                    </section>

                    <section class="rafiq-pg-section" v-if="links.length">
                        <h3 class="rafiq-pg-section-title">
                            <iconify-icon icon="solar:link-round-angle-line-duotone"></iconify-icon>
                            صفحات ذات صلة
                        </h3>
                        <div class="rafiq-pg-related">
                            <a href="javascript:void(0)"
                               class="rafiq-pg-related-chip"
                               v-for="link in links"
                               :key="link.path"
                               @click="go(link.path)">
                                <iconify-icon :icon="link.icon"></iconify-icon>
                                <span>{{ link.label }}</span>
                            </a>
                        </div>
                    </section>

                    <p class="rafiq-pg-empty" v-if="!guide.documented">
                        لم يُكتب شرح مفصّل لهذه الصفحة بعد.
                    </p>
                </div>

                <div class="rafiq-pg-modal-foot">
                    <span class="rafiq-pg-hint">
                        <iconify-icon icon="solar:lightbulb-line-duotone"></iconify-icon>
                        مرّر الماوس فوق أي صفحة في القائمة الجانبية لترى شرحها المختصر قبل فتحها.
                    </span>
                    <button type="button" class="btn btn-primary btn-sm" @click="close">فهمت</button>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
    import { guideFor, relatedLinks } from '../../Shared/pageGuides.js';

    export default {
        name: 'PageGuide',

        data() {
            return { open: false };
        },

        computed: {
            guide() {
                return guideFor(this.$route ? this.$route.path : '');
            },
            links() {
                return relatedLinks(this.guide);
            }
        },

        watch: {
            // Navigating away must not leave a guide for the previous page open.
            '$route.path'() {
                this.open = false;
            }
        },

        mounted() {
            document.addEventListener('keydown', this.onKeydown);
        },

        beforeDestroy() {
            document.removeEventListener('keydown', this.onKeydown);
            document.body.classList.remove('rafiq-pg-open');
        },

        methods: {
            openGuide() {
                this.open = true;
                document.body.classList.add('rafiq-pg-open');
            },

            close() {
                this.open = false;
                document.body.classList.remove('rafiq-pg-open');
            },

            go(path) {
                this.close();
                if (path !== this.$router.currentRoute.path) this.$router.push(path);
            },

            onKeydown(e) {
                if (e.key === 'Escape' && this.open) this.close();
            }
        }
    };
</script>

<style>
    /* ============================================================
       Page guide — header strip + (i) modal.
       Deliberately NOT scoped: the bar sits above <router-view> and has to
       line up with the .container-fluid padding every page already uses.
       ============================================================ */

    /* The topbar is `position: fixed`, and every page's own wrapper carries a
       top margin to clear it. Since the bar now sits ABOVE that wrapper, it
       takes over the offset and the wrapper's copy is dropped — otherwise the
       bar hides behind the header and the page gains 72px of dead space. */
    .rafiq-pg {
        margin-top: 72px;
        padding: 18px 24px 0;
    }

    .rafiq-pg + * {
        margin-top: 0 !important;
    }

    @media (max-width: 991.98px) {
        .rafiq-pg {
            margin-top: 80px; /* matches the framework's own smaller-screen offset */
        }
    }

    .rafiq-pg-bar {
        display: flex;
        align-items: center;
        gap: 14px;
        padding: 12px 18px;
        border: 1px solid var(--bs-border-color, #eef0f4);
        border-radius: 12px;
        background: var(--bs-card-bg, #fff);
    }

    .rafiq-pg-avatar {
        flex: 0 0 auto;
        width: 42px;
        height: 42px;
        border-radius: 11px;
        display: flex;
        align-items: center;
        justify-content: center;
        background: rgba(93, 135, 255, 0.10);
        color: #5d87ff;
        font-size: 22px;
    }

        .rafiq-pg-avatar.lg {
            width: 52px;
            height: 52px;
            font-size: 27px;
        }

    .rafiq-pg-head {
        min-width: 0;
        flex: 1 1 auto;
    }

    .rafiq-pg-titleline {
        display: flex;
        align-items: center;
        gap: 8px;
    }

    .rafiq-pg-title {
        margin: 0;
        font-size: 1.05rem;
        font-weight: 600;
        line-height: 1.4;
        color: var(--bs-body-color, #2a3547);
    }

    /* The small info button. Muted until hovered so it never competes with the
       page's own actions, but always in the identical spot on every page. */
    .rafiq-pg-info {
        flex: 0 0 auto;
        width: 24px;
        height: 24px;
        padding: 0;
        border: 0;
        border-radius: 50%;
        display: inline-flex;
        align-items: center;
        justify-content: center;
        font-size: 17px;
        line-height: 1;
        background: transparent;
        color: #8a94a6;
        cursor: pointer;
        transition: background .15s ease, color .15s ease, transform .15s ease;
    }

        .rafiq-pg-info:hover,
        .rafiq-pg-info:focus-visible,
        .rafiq-pg-info.active {
            background: rgba(93, 135, 255, 0.12);
            color: #5d87ff;
            transform: scale(1.08);
            outline: none;
        }

    .rafiq-pg-tagline {
        margin: 2px 0 0;
        font-size: 0.8rem;
        color: var(--bs-secondary-color, #8a94a6);
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
    }

    .rafiq-pg-group {
        flex: 0 0 auto;
        font-size: 0.72rem;
        padding: 4px 10px;
        border-radius: 30px;
        background: rgba(93, 135, 255, 0.08);
        color: #5d87ff;
    }

    @media (max-width: 767.98px) {
        .rafiq-pg {
            padding: 12px 12px 0;
        }

        .rafiq-pg-group {
            display: none;
        }
    }

    /* ===================== Modal ===================== */

    .rafiq-pg-backdrop {
        position: fixed;
        inset: 0;
        z-index: 2000;
        background: rgba(20, 26, 38, 0.45);
        display: flex;
        align-items: center;
        justify-content: center;
        padding: 24px;
        animation: rafiq-pg-fade .15s ease;
    }

    @keyframes rafiq-pg-fade {
        from {
            opacity: 0;
        }

        to {
            opacity: 1;
        }
    }

    .rafiq-pg-modal {
        width: 100%;
        max-width: 760px;
        max-height: 88vh;
        display: flex;
        flex-direction: column;
        border-radius: 16px;
        background: var(--bs-card-bg, #fff);
        box-shadow: 0 24px 60px rgba(20, 26, 38, 0.25);
        overflow: hidden;
        animation: rafiq-pg-rise .18s ease;
    }

    @keyframes rafiq-pg-rise {
        from {
            transform: translateY(14px);
            opacity: 0;
        }

        to {
            transform: none;
            opacity: 1;
        }
    }

    .rafiq-pg-modal-head {
        display: flex;
        align-items: center;
        gap: 14px;
        padding: 18px 22px;
        border-bottom: 1px solid var(--bs-border-color, #eef0f4);
    }

    .rafiq-pg-modal-headtext {
        flex: 1 1 auto;
        min-width: 0;
    }

    .rafiq-pg-modal-title {
        margin: 0;
        font-size: 1.15rem;
        font-weight: 600;
        color: var(--bs-body-color, #2a3547);
    }

    .rafiq-pg-modal-sub {
        margin: 3px 0 0;
        font-size: 0.82rem;
        color: var(--bs-secondary-color, #8a94a6);
    }

    .rafiq-pg-close {
        flex: 0 0 auto;
        border: 0;
        background: transparent;
        color: #8a94a6;
        font-size: 24px;
        line-height: 1;
        cursor: pointer;
        padding: 2px;
        border-radius: 8px;
    }

        .rafiq-pg-close:hover {
            color: #fa896b;
        }

    .rafiq-pg-modal-body {
        padding: 20px 22px;
        overflow-y: auto;
    }

    .rafiq-pg-what {
        margin: 0 0 18px;
        font-size: 0.92rem;
        line-height: 1.9;
        color: var(--bs-body-color, #2a3547);
        padding: 14px 16px;
        border-radius: 12px;
        background: rgba(93, 135, 255, 0.06);
        border-right: 3px solid #5d87ff;
    }

    .rafiq-pg-section {
        margin-bottom: 18px;
    }

        .rafiq-pg-section:last-child {
            margin-bottom: 0;
        }

    .rafiq-pg-section-title {
        display: flex;
        align-items: center;
        gap: 7px;
        margin: 0 0 9px;
        font-size: 0.86rem;
        font-weight: 600;
        color: #5d87ff;
    }

        .rafiq-pg-section-title.warn {
            color: #ffae1f;
        }

    .rafiq-pg-list {
        margin: 0;
        padding: 0;
        list-style: none;
    }

        .rafiq-pg-list li {
            position: relative;
            padding: 5px 20px 5px 0;
            font-size: 0.87rem;
            line-height: 1.8;
            color: var(--bs-body-color, #4a5468);
        }

            .rafiq-pg-list li::before {
                content: '';
                position: absolute;
                top: 15px;
                right: 6px;
                width: 6px;
                height: 6px;
                border-radius: 50%;
                background: #a7b3cc;
            }

        .rafiq-pg-list.do li::before {
            background: #13deb9;
        }

        .rafiq-pg-list.warn li::before {
            background: #ffae1f;
        }

    .rafiq-pg-related {
        display: flex;
        flex-wrap: wrap;
        gap: 8px;
    }

    .rafiq-pg-related-chip {
        display: inline-flex;
        align-items: center;
        gap: 6px;
        padding: 6px 12px;
        border-radius: 30px;
        font-size: 0.8rem;
        text-decoration: none;
        color: #5d87ff;
        background: rgba(93, 135, 255, 0.08);
        transition: background .15s ease;
    }

        .rafiq-pg-related-chip:hover {
            background: rgba(93, 135, 255, 0.18);
            color: #4570ea;
        }

    .rafiq-pg-empty {
        margin: 0;
        font-size: 0.85rem;
        color: var(--bs-secondary-color, #8a94a6);
    }

    .rafiq-pg-modal-foot {
        display: flex;
        align-items: center;
        justify-content: space-between;
        gap: 12px;
        padding: 14px 22px;
        border-top: 1px solid var(--bs-border-color, #eef0f4);
        background: var(--bs-tertiary-bg, #f8f9fc);
    }

    .rafiq-pg-hint {
        display: inline-flex;
        align-items: center;
        gap: 6px;
        font-size: 0.76rem;
        color: var(--bs-secondary-color, #8a94a6);
    }

    body.rafiq-pg-open {
        overflow: hidden;
    }
</style>
